import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import '../Customer/Customer.css';

const CompanyEdit = ({ match }) => {
    const recordId = match.params.id;
    const history = useHistory();
    const { isAuthenticated, companyId, isSuperAdmin, logout, token } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    const [formData, setFormData] = useState({
        Name: '',
        CompanyCode: '',
        ContactNumber1: '',
        ContactNumber2: '',
        Email: '',
        GSTNumber: '',
        AddressLine1: '',
        AddressLine2: '',
        Location: '',
        PaymentQRcodeUPIID: '',
        UPIName: '',
        Cashinhand: '',
    });

    useEffect(() => {
        fetchData('CompanyApi', `/CompanyDetails/${recordId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Company Details:', error);
            });
    }, [recordId]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setIsLoading(true);
        setErrorMessage('');

        if (!formData.Name) {
            setErrorMessage('Company Name is required!');
            setIsLoading(false);
            return;
        }

        const updatedData = {
            ...formData,
            UpdatedBy: 1,
        };

        updateData('CompanyApi', `${recordId}`, updatedData, token)
            .then(() => {
                setSuccessMessage('Company updated successfully!');
                history.push('/companyIndex');
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setErrorMessage('Failed to update company.');
                setIsLoading(false);
            });
    };

    return (
        <div className="form-container">
            <div className="form-card">
                {/* Header */}
                <div className="form-header">
                    <h1 className="form-title">Edit Company</h1>
                    <div className="form-actions">
                        <Link to="/companyIndex" className="btn-back">
                            <ArrowBackIcon fontSize="small" />
                            Back to List
                        </Link>
                        <button className="btn-save" onClick={handleSubmit} disabled={isLoading}>
                            {isLoading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <>
                                    <SaveIcon fontSize="small" />
                                    Update
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Messages */}
                {errorMessage && <div className="message error">{errorMessage}</div>}
                {successMessage && <div className="message success">{successMessage}</div>}

                {/* Form Body */}
                <div className="form-body">
                    {/* Row 1 - Company Name & Code */}
                    <div className="form-row">
                        <div className="form-group" style={{ flex: 3 }}>
                            <label className="form-label">Company Name *</label>
                            <input
                                className="form-input"
                                name="Name"
                                value={formData.Name || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter company name"
                                maxLength={50}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Company Code</label>
                            <input
                                className="form-input"
                                name="CompanyCode"
                                value={formData.CompanyCode || ''}
                                type="text"
                                readOnly
                                style={{ backgroundColor: '#f8fafc' }}
                            />
                        </div>
                    </div>

                    {/* Row 2 - Contact Details */}
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Contact Number 1 *</label>
                            <input
                                className="form-input"
                                name="ContactNumber1"
                                value={formData.ContactNumber1 || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter contact number"
                                maxLength={15}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Contact Number 2</label>
                            <input
                                className="form-input"
                                name="ContactNumber2"
                                value={formData.ContactNumber2 || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter contact number"
                                maxLength={15}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Email</label>
                            <input
                                className="form-input"
                                name="Email"
                                value={formData.Email || ''}
                                onChange={handleChange}
                                type="email"
                                placeholder="Enter email"
                                maxLength={30}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">GST Number</label>
                            <input
                                className="form-input"
                                name="GSTNumber"
                                value={formData.GSTNumber || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter GST number"
                                maxLength={15}
                            />
                        </div>
                    </div>

                    {/* Row 3 - UPI Details (Super Admin Only) */}
                    {isSuperAdmin && (
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Payment QR UPI ID</label>
                                <input
                                    className="form-input"
                                    name="PaymentQRcodeUPIID"
                                    value={formData.PaymentQRcodeUPIID || ''}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter UPI ID"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Payment QR UPI Name</label>
                                <input
                                    className="form-input"
                                    name="UPIName"
                                    value={formData.UPIName || ''}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter UPI name"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Cash In Hand</label>
                                <input
                                    className="form-input"
                                    name="Cashinhand"
                                    value={formData.Cashinhand || ''}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter cash in hand"
                                    style={{ textAlign: 'right' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Row 4 - Address */}
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Address Line 1 *</label>
                            <input
                                className="form-input"
                                name="AddressLine1"
                                value={formData.AddressLine1 || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter address line 1"
                                maxLength={200}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Address Line 2</label>
                            <input
                                className="form-input"
                                name="AddressLine2"
                                value={formData.AddressLine2 || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter address line 2"
                                maxLength={200}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanyEdit;
